import { Helmet } from 'react-helmet-async';
import { getTranslateLang } from '@/lib/translateUtils';

interface ReviewProps {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  worstRating?: number;
}

interface ProductSchemaProps {
  name: string;
  description: string;
  image: string;
  price: string;
  currency: string;
  availability: 'InStock' | 'OutOfStock' | 'PreOrder';
  sku?: string;
  brand?: string;
  url: string;
  reviews?: ReviewProps;
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface SiteNavigationItem {
  name: string;
  url: string;
}

interface HreflangLink {
  lang: string;
  href: string;
}

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'product' | 'article';
  noindex?: boolean;
  product?: ProductSchemaProps;
  breadcrumbs?: BreadcrumbItem[];
  faqItems?: FAQItem[];
  showOrganization?: boolean;
  showWebsiteSchema?: boolean;
  siteNavigation?: SiteNavigationItem[];
  hreflangLinks?: HreflangLink[];
  lang?: 'he' | 'en' | 'ar' | 'ru';
}

const SITE_URL = 'https://alice.jewelry';
const DEFAULT_OG_IMAGE = 'https://storage.googleapis.com/gpt-engineer-file-uploads/ZSufjNmpnwSz0xRwTufg0bdkYfw1/social-images/social-1766485157151-פרופיל גוגל אליס.png';

// Dynamic SITE_NAME per language
const SITE_NAMES: Record<string, string> = {
  he: 'אליס | תכשיטים ושעונים',
  en: 'Alice | Jewellery & Watches',
  ru: 'Алиса | Ювелирные изделия и часы',
  ar: 'أليس | مجوهرات وساعات',
};

// Dynamic organization descriptions
const ORG_DESCRIPTIONS: Record<string, string> = {
  he: 'חברת התכשיטים המובילה בבאר שבע בדרום מאז 1984. באתר התכשיטים אונליין - מבחר ענק של טבעות אירוסין, טבעות נישואין, צמידים, שרשראות, תליונים, עגילים.',
  en: 'Leading jewellery company in Beer Sheva since 1984. Online jewellery store – huge selection of engagement rings, wedding rings, bracelets, necklaces, pendants & earrings.',
  ru: 'Ведущая ювелирная компания в Беэр-Шеве с 1984 года. Интернет-магазин – помолвочные и обручальные кольца, браслеты, цепочки, подвески и серьги.',
  ar: 'شركة المجوهرات الرائدة في بئر السبع منذ 1984. متجر أونلاين – خواتم خطبة وزواج وأساور وقلائد وأقراط.',
};

// Dynamic navigation per language
const SITE_NAVIGATIONS: Record<string, SiteNavigationItem[]> = {
  he: [
    { name: 'עגילי זהב', url: '/collections/עגילים' },
    { name: 'שרשראות עם תליון', url: '/collections/שרשראות' },
    { name: 'טבעות אליס', url: '/collections/טבעות' },
    { name: 'צמידים', url: '/collections/צמידים' },
    { name: 'בלוג תכשיטים', url: '/blog' },
    { name: 'חנות תכשיטים בבאר שבע, הרצל 58', url: '/contact' },
    { name: 'שאלות נפוצות', url: '/faq' },
    { name: 'אודות', url: '/about' },
  ],
  en: [
    { name: 'Gold Earrings', url: '/collections/עגילים' },
    { name: 'Necklaces with Pendant', url: '/collections/שרשראות' },
    { name: 'Alice Rings', url: '/collections/טבעות' },
    { name: 'Bracelets', url: '/collections/צמידים' },
    { name: 'Jewellery Blog', url: '/blog' },
    { name: 'Jewellery Store in Beer Sheva, Herzl 58', url: '/contact' },
    { name: 'FAQ', url: '/faq' },
    { name: 'About', url: '/about' },
  ],
  ru: [
    { name: 'Золотые серьги', url: '/collections/עגילים' },
    { name: 'Цепочки с подвеской', url: '/collections/שרשראות' },
    { name: 'Кольца Алиса', url: '/collections/טבעות' },
    { name: 'Браслеты', url: '/collections/צמידים' },
    { name: 'Блог о ювелирных изделиях', url: '/blog' },
    { name: 'Ювелирный магазин в Беэр-Шеве, Герцля 58', url: '/contact' },
    { name: 'Вопросы и ответы', url: '/faq' },
    { name: 'О нас', url: '/about' },
  ],
  ar: [
    { name: 'أقراط ذهبية', url: '/collections/עגילים' },
    { name: 'قلائد مع تعليقة', url: '/collections/שרשראות' },
    { name: 'خواتم أليس', url: '/collections/טבעות' },
    { name: 'أساور', url: '/collections/צמידים' },
    { name: 'مدوّنة المجوهرات', url: '/blog' },
    { name: 'متجر مجوهرات في بئر السبع، هرتسل 58', url: '/contact' },
    { name: 'أسئلة شائعة', url: '/faq' },
    { name: 'عن أليس', url: '/about' },
  ],
};

// Detect current language
function getCurrentLang(): 'he' | 'en' | 'ar' | 'ru' {
  const lang = getTranslateLang();
  if (lang === 'en' || lang === 'ru' || lang === 'ar') return lang;
  return 'he';
}

const LANG_TO_LOCALE: Record<string, string> = {
  he: 'he-IL',
  en: 'en-US',
  ru: 'ru-RU',
  ar: 'ar-SA',
};

// Default hreflang configuration – all supported languages
const getDefaultHreflangLinks = (path: string): HreflangLink[] => [
  { lang: 'he', href: `${SITE_URL}${path}` },
  { lang: 'en', href: `${SITE_URL}${path}` },
  { lang: 'ru', href: `${SITE_URL}${path}` },
  { lang: 'ar', href: `${SITE_URL}${path}` },
  { lang: 'x-default', href: `${SITE_URL}${path}` },
];

export function SEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  noindex = false,
  product,
  breadcrumbs,
  faqItems,
  showOrganization = false,
  showWebsiteSchema = false,
  siteNavigation,
  hreflangLinks,
  lang,
}: SEOProps) {
  // Auto-detect language if not provided
  const currentLang = lang || getCurrentLang();
  const SITE_NAME = SITE_NAMES[currentLang] || SITE_NAMES.he;
  
  const fullTitle = title.includes(SITE_NAME) || title.includes('אליס') || title.includes('Alice') || title.includes('Алиса') || title.includes('أليس')
    ? title 
    : `${title} | ${SITE_NAME}`;
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  // IMPORTANT: Always build canonical from SITE_URL to avoid duplicate content issues
  // Google Search Console flags "Duplicate without user-selected canonical" when canonical
  // points to preview/staging domains instead of the production domain
  const canonicalUrl = canonical || `${SITE_URL}${currentPath}`;
  const ogImageUrl = ogImage || DEFAULT_OG_IMAGE;
  
  const finalHreflangLinks = hreflangLinks || getDefaultHreflangLinks(currentPath);

  // Build organization schema with dynamic language
  const organizationSchema = showOrganization ? {
    '@context': 'https://schema.org',
    '@type': 'JewelryStore',
    '@id': `${SITE_URL}/#organization`,
    name: 'אליס תכשיטים ושעונים',
    alternateName: ['Alice Jewellery', 'תכשיטי אליס', 'אליס תכשיטים'],
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/images/logo-alice-print.png`,
      width: 512,
      height: 512,
    },
    image: DEFAULT_OG_IMAGE,
    description: ORG_DESCRIPTIONS[currentLang] || ORG_DESCRIPTIONS.he,
    slogan: 'תכשיטים יוקרתיים מאז 1984',
    telephone: '+972-79-5575576',
    email: 'info@alicejewelry.net',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'רחוב הרצל 58',
      addressLocality: 'באר שבע',
      postalCode: '8423518',
      addressRegion: 'South',
      addressCountry: 'IL',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 31.241175,
      longitude: 34.792080,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Sunday', 'Monday', 'Wednesday', 'Thursday'],
        opens: '10:00',
        closes: '19:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Tuesday',
        opens: '09:45',
        closes: '14:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Friday',
        opens: '09:45',
        closes: '14:00',
      },
    ],
    priceRange: '₪₪-₪₪₪₪',
    currenciesAccepted: 'ILS',
    paymentAccepted: 'Cash, Credit Card, PayPal',
    foundingDate: '1984',
    founder: {
      '@type': 'Person',
      name: 'דוד בורמד',
    },
    sameAs: [
      'https://www.facebook.com/alicejewelry',
      'https://www.instagram.com/alice_jewelry_',
    ],
    hasMap: 'https://www.google.com/maps/place/%D7%90%D7%9C%D7%99%D7%A1+%7C+%D7%AA%D7%9B%D7%A9%D7%99%D7%98%D7%99%D7%9D+%D7%95%D7%A8%D7%A7%D7%95%D7%A8%D7%99%D7%9D/@31.241175,34.792080,17z',
    areaServed: {
      '@type': 'Country',
      name: 'Israel',
    },
    knowsAbout: [
      'טבעות אירוסין',
      'טבעות נישואין',
      'חריטות ועיצוב אישי',
      'תכשיטי ילדים',
      'שרשראות זהב',
      'עגילי זהב',
      'צמידי זהב',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'קטלוג תכשיטים',
      itemListElement: [
        { '@type': 'OfferCatalog', name: 'עגילי זהב', url: `${SITE_URL}/collections/עגילים` },
        { '@type': 'OfferCatalog', name: 'שרשראות עם תליון', url: `${SITE_URL}/collections/שרשראות` },
        { '@type': 'OfferCatalog', name: 'טבעות אליס', url: `${SITE_URL}/collections/טבעות` },
        { '@type': 'OfferCatalog', name: 'צמידים', url: `${SITE_URL}/collections/צמידים` },
      ],
    },
  } : null;

  // WebSite schema with dynamic language
  const websiteSchema = showWebsiteSchema
    ? {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'Alice Jewellery',
        alternateName: 'אליס תכשיטים',
        description: ORG_DESCRIPTIONS[currentLang] || ORG_DESCRIPTIONS.he,
        publisher: { '@id': `${SITE_URL}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${SITE_URL}/shop?search={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
        inLanguage: LANG_TO_LOCALE[currentLang] || 'he-IL',
      }
    : null;

  // Site navigation schema with dynamic language
  const navItems = siteNavigation || SITE_NAVIGATIONS[currentLang] || SITE_NAVIGATIONS.he;
  const siteNavigationSchema = showWebsiteSchema
    ? {
        '@context': 'https://schema.org',
        '@graph': navItems.map((item, index) => ({
          '@type': 'SiteNavigationElement',
          '@id': `${SITE_URL}/#navigation-${index}`,
          name: item.name,
          url: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
        })),
      }
    : null;

  // Product schema
  const productSchema = product
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        description: product.description,
        image: product.image,
        sku: product.sku,
        brand: product.brand ? { '@type': 'Brand', name: product.brand } : undefined,
        offers: {
          '@type': 'Offer',
          url: product.url,
          priceCurrency: product.currency,
          price: product.price,
          availability: `https://schema.org/${product.availability}`,
          seller: { '@type': 'Organization', name: 'Alice Jewellery' },
        },
        ...(product.reviews && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: product.reviews.ratingValue,
            reviewCount: product.reviews.reviewCount,
            bestRating: product.reviews.bestRating || 5,
            worstRating: product.reviews.worstRating || 1,
          },
        }),
      }
    : null;

  // Breadcrumb schema
  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
          })),
        }
      : null;

  // FAQ schema
  const faqSchema =
    faqItems && faqItems.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
          })),
        }
      : null;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      <html lang={currentLang} dir={currentLang === 'he' || currentLang === 'ar' ? 'rtl' : 'ltr'} />
      
      {finalHreflangLinks.map((link) => (
        <link key={link.lang} rel="alternate" hrefLang={link.lang} href={link.href} />
      ))}
      
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={LANG_TO_LOCALE[currentLang] || 'he_IL'} />
      <meta property="og:locale:alternate" content="he_IL" />
      <meta property="og:locale:alternate" content="en_US" />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      
      {product && (
        <>
          <meta property="product:price:amount" content={product.price} />
          <meta property="product:price:currency" content={product.currency} />
          <meta property="product:availability" content={product.availability.toLowerCase()} />
        </>
      )}
      
      {organizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}
      
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
      
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      
      {websiteSchema && (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      )}
      
      {siteNavigationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(siteNavigationSchema)}
        </script>
      )}
    </Helmet>
  );
}

export { SITE_URL, DEFAULT_OG_IMAGE };
export const SITE_NAME = SITE_NAMES.he;
